import { DataTable } from '@/components/data-table/DataTable';
import { LpohPagination } from '@/components/pagination/LpohPagination';
import { PageSize } from '@/components/pagination/PageSize';
import { Params } from '@/utils/types';
import { ColumnDef } from '@tanstack/react-table';
import { useLoaderData, useNavigation, useSearchParams } from 'react-router';
import {
	FieldReport,
	DeploymentTypeEnum,
	deploymentTypeLabels,
	SWAP_REASONS_LABELS
} from '@/routes/measurement-group/field-report/types.ts';
import { PAGE_SIZES, DEFAULT_PAGE_SIZE } from './constants';
import { format } from 'date-fns';
import { DEFAULT_DATE_TIME_FORMAT } from '@/utils/dates';
import { maybeCellValue } from '@/utils/maybe-cell-value';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { FieldReportsListLoaderResponse } from './types';
import useResetTableStateOnChange from '@/hooks/useResetTableState.ts';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb.tsx';
import { BreadcrumbRouterLink } from '@/components/link/Link.tsx';
import { ROUTES } from '@/constants.ts';
import { FieldReportsListFilters } from './FieldReportsListFilters.tsx';
import useFieldReportsFilters from './useFieldReportsFilters.ts';
import { Button } from '@/components/ui/button.tsx';
import apiClient from '@/api.ts';
import { MEASUREMENT_GROUPS_BASE_API_URL } from '@/routes/measurement-group/constants.ts';
import { toast } from 'sonner';
import useTitle from '@/hooks/useTitle.ts';

export const FieldReportsList = () => {
	useTitle("Field Reports");

	const [searchParams] = useSearchParams();
	const navigation = useNavigation();
	const isNavigating = !!navigation.location;
	const page = searchParams.get(Params.page) || '1';
	const limit = searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
	const search = searchParams.get(Params.search) || '';

	const { fieldReportsList } = useLoaderData() as FieldReportsListLoaderResponse;

	const { items: fieldReports, totalPages } = fieldReportsList.data;

	useResetTableStateOnChange({ observed: { page, limit, search } });

	const { handleSearchChange, handleDeploymentTypeChange, handleSwapReasonChange, handleHasExternalAntennasChange } = useFieldReportsFilters();

	const columns: ColumnDef<FieldReport>[] = [
		{
			accessorKey: 'msId',
			header: 'Device ID'
		},
		{
			accessorKey: 'siteName',
			header: 'Site'
		},
		{
			accessorKey: 'staffName',
			header: 'Staff'
		},
		{
			accessorKey: 'performedAt',
			header: 'Date',
			cell: ({ row }) => maybeCellValue(format(row.original.performedAt, DEFAULT_DATE_TIME_FORMAT))
		},
		{
			accessorKey: 'deploymentType',
			header: 'Type',
			cell: ({ row }) => deploymentTypeLabels[row.original.deploymentType as keyof typeof DeploymentTypeEnum]
		},
		{
			accessorKey: 'summary',
			header: 'Summary',
			cell: ({ row }) => (
				<Tooltip>
					<TooltipTrigger asChild>
						<div className="max-w-md truncate cursor-default" title={row.original.notes}>
							{row.original.notes}
						</div>
					</TooltipTrigger>
					<TooltipContent className="max-w-xs p-2">{row.original.notes}</TooltipContent>
				</Tooltip>
			)
		},
		{
			accessorKey: 'swapReason',
			header: 'Swap reason',
			cell: ({ row }) =>
				maybeCellValue(row.original.swapReason ? SWAP_REASONS_LABELS[row.original.swapReason] : null)
		},
		{
			accessorKey: 'hasExternalAntennas',
			header: 'External antennas',
			cell: ({ row }) => maybeCellValue(row.original.hasExternalAntennas)
		}
	];

	async function handleDownloadCsv() {
		try {
			const response = await apiClient.get(`${MEASUREMENT_GROUPS_BASE_API_URL}/field-reports?csv=true`, {
				responseType: 'blob'
			});
			const disposition = response.headers['content-disposition'];
			const fileNameMatch = disposition && disposition.match(/filename="?([^"]+)"?/);
			const fileName = fileNameMatch ? fileNameMatch[1] : 'export.csv';
			const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			toast.error(`Failed to download CSV`);
		}
	}

	return (
		<div className="flex flex-col gap-10">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>Field Reports</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<div className="w-full flex gap-2 flex-col">
				<div className="flex justify-between md:flex-row flex-col-reverse gap-2 items-end">
					<FieldReportsListFilters handleSearchChange={handleSearchChange} handleDeploymentTypeChange={handleDeploymentTypeChange} handleSwapReasonChange={handleSwapReasonChange} handleHasExternalAntennasChange={handleHasExternalAntennasChange} />
					<Button type="button" onClick={() => handleDownloadCsv()}>
						Download CSV
					</Button>
				</div>

				<DataTable columns={columns} data={fieldReports} isLoading={isNavigating} />

				<div className="flex justify-between">
					<PageSize pageSize={limit} options={PAGE_SIZES} />
					<LpohPagination currentPage={parseInt(page)} totalPages={totalPages} />
				</div>
			</div>
		</div>
	);
};
